import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { useLogoutAcrossTabs } from './hooks/useLogoutAcrossTabs';
import { authorizeRequest } from './requests/authorize';
import type { UserAuthContextValue } from './types';
import { useAnalytics } from '~/contexts/AnalyticsProvider/useAnalytics';
import { useIdentifyLDUser } from '~/contexts/FeatureFlagProvider';
import { useIdentifySentryUser } from '~/contexts/MonitoringProvider';

// Constants
const defaultContextValue: UserAuthContextValue = {
  status: 'loading',
  accessToken: null,
  user: null,
  error: null,
};

// Contexts
export const UserAuthContext = createContext<UserAuthContextValue>(
  defaultContextValue,
);

// Exports
type Props = { children: React.ReactNode; enableCrossTabLogout?: boolean };
export const UserAuthProvider = ({
  children,
  enableCrossTabLogout = false,
}: Props) => {
  const authorizeQuery = useQuery({
    queryKey: ['userAuthProvider.authorize'],
    queryFn: authorizeRequest,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const accessToken = authorizeQuery.data?.accessToken ?? null;
  const user = authorizeQuery.data?.user ?? null;

  // Derive user auth status
  const status = useMemo(() => {
    // Still fetching user auth state
    if (authorizeQuery.isLoading) return 'loading';
    // Backend or response validation errors
    if (authorizeQuery.error) return 'error';
    // Backend responded with auth status ("logged-in" or "logged-out")
    if (authorizeQuery.data) return authorizeQuery.data.status;
    // If we end up here, something went very wrong
    Sentry.captureException(new Error('Failed to determine user auth status'));
    return 'error';
  }, [authorizeQuery.isLoading, authorizeQuery.error, authorizeQuery.data]);

  // Identify users with various third-party tools
  const { identifySentryUser } = useIdentifySentryUser();
  const { identifyLDUser } = useIdentifyLDUser();
  const analytics = useAnalytics();
  useEffect(() => {
    if (user?.userType) {
      identifySentryUser(user);
      identifyLDUser(user);
      analytics.identify(user);
    } else {
      analytics.reset();
    }
  }, [identifyLDUser, identifySentryUser, user, analytics]);

  // Log users in/out across all tabs
  useLogoutAcrossTabs(enableCrossTabLogout);

  // Assemble the context value
  const contextValue: UserAuthContextValue = useMemo(() => {
    if (status === 'error' && authorizeQuery.error) {
      return {
        status,
        user: null,
        accessToken: null,
        error: authorizeQuery.error,
      };
    }
    if (status === 'logged-out') {
      return { status, accessToken: null, user: null, error: null };
    }
    if (status === 'logged-in' && user && accessToken) {
      return { status, accessToken, user, error: null };
    }
    if (status === 'loading') {
      return { status, accessToken: null, user: null, error: null };
    }

    throw Error('Invalid user auth status');
  }, [accessToken, authorizeQuery.error, status, user]);

  // Logging state for debugging purposes
  useEffect(() => {
    if (!commonUtils.EnvUtils.isCustomerFacing()) {
      // eslint-disable-next-line no-console
      console.log('[UserAuthProvider] Context change', contextValue);
    }
  }, [contextValue]);

  return (
    <UserAuthContext.Provider value={contextValue}>
      {children}
    </UserAuthContext.Provider>
  );
};

/**
 * Directly access the UserAuthProvider context. User might not be logged in yet, or at all, on
 * un-authenticated routes. You should use `status` to handle loading and error states explicitly.
 *
 * If you are working in a part of the app where user is known to be logged in, you should use the
 * `useUserAuthOrThrow` hook instead.
 */
export const useUserAuthUnsafe = () => useContext(UserAuthContext);

/**
 * Returns logged-in user auth context. Throws an error if user is not logged-in.
 *
 * If you are working in a part of the app where the user is not logged in, or you need to handle
 * loading -> logged-in transitions, use the `useUserAuthUnsafe` hook instead.
 */
export const useUserAuthOrThrow = () => {
  const userAuth = useContext(UserAuthContext);
  if (userAuth.status !== 'logged-in') {
    throw Error('Expected user to be logged in in call to useUserAuthOrThrow');
  }
  return userAuth;
};
